import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users,
  MessageSquare,
  Store,
  AlertTriangle,
  TrendingUp,
  Award,
  Link2,
  RefreshCw,
  PackageCheck,
  SearchX,
} from 'lucide-react';
import AdminLayout from './AdminLayout';

interface DashboardStats {
  totalUsers: number;
  totalReviews: number;
  totalRestaurants: number;
  pendingReviews: number;
  flaggedReviews: number;
  totalKudozAwarded: number;
  activeUsers7d: number;
  newUsersToday: number;
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  return { start: isoDate(start), end: isoDate(end) };
}

interface EatzyOrdersBySourceRow {
  source: string;
  channel?: string;
  count: number;
  revenue?: number | string;
  [key: string]: unknown;
}

interface EatzySignupsBySourceRow {
  source: string;
  channel?: string;
  count: number;
  [key: string]: unknown;
}

interface EatzyOrdersBySourceResponse {
  data?: EatzyOrdersBySourceRow[];
  results?: EatzyOrdersBySourceRow[];
  [key: string]: unknown;
}

interface EatzySignupsBySourceResponse {
  data?: EatzySignupsBySourceRow[];
  results?: EatzySignupsBySourceRow[];
  [key: string]: unknown;
}

function resolveRows<T>(res: unknown): T[] {
  if (!res || typeof res !== 'object') return [];
  const r = res as Record<string, unknown>;
  if (Array.isArray(r.data)) return r.data as T[];
  if (Array.isArray(r.results)) return r.results as T[];
  if (Array.isArray(r)) return r as T[];
  return [];
}

function TableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
      <SearchX className="w-8 h-8" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function EatzyAttributionPanel() {
  const defaults = defaultDateRange();
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [fetchKey, setFetchKey] = useState<{ start: string; end: string } | null>(null);

  const ordersQuery = useQuery<EatzyOrdersBySourceResponse>({
    queryKey: ['/api/admin/eatzy/attribution/orders-by-source', fetchKey?.start, fetchKey?.end],
    enabled: !!fetchKey,
    retry: false,
    queryFn: fetchKey
      ? () => fetch(
          `/api/admin/eatzy/attribution/orders-by-source?start=${encodeURIComponent(fetchKey.start)}&end=${encodeURIComponent(fetchKey.end)}`,
          { credentials: 'include', headers: { Accept: 'application/json', ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}) } },
        ).then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      : undefined,
  });

  const signupsQuery = useQuery<EatzySignupsBySourceResponse>({
    queryKey: ['/api/admin/eatzy/attribution/signups-by-source', fetchKey?.start, fetchKey?.end],
    enabled: !!fetchKey,
    retry: false,
    queryFn: fetchKey
      ? () => fetch(
          `/api/admin/eatzy/attribution/signups-by-source?start=${encodeURIComponent(fetchKey.start)}&end=${encodeURIComponent(fetchKey.end)}`,
          { credentials: 'include', headers: { Accept: 'application/json', ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}) } },
        ).then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      : undefined,
  });

  const isFetching = ordersQuery.isFetching || signupsQuery.isFetching;
  const hasError = ordersQuery.isError || signupsQuery.isError;
  const hasFetched = !!fetchKey;

  const orderRows = resolveRows<EatzyOrdersBySourceRow>(ordersQuery.data);
  const signupRows = resolveRows<EatzySignupsBySourceRow>(signupsQuery.data);

  function handleFetch() {
    setFetchKey({ start, end });
  }

  return (
    <Card data-testid="card-eatzy-cross-reference">
      <CardHeader>
        <div className="flex flex-row items-center gap-2 flex-wrap">
          <Link2 className="w-5 h-5 text-primary flex-shrink-0" />
          <CardTitle>Eatzy Attribution Cross-Reference</CardTitle>
        </div>
        <CardDescription>
          Orders and signups attributed to Kudoz-originated traffic, sourced directly from Eatzy.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Date range controls */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1.5">
            <Label htmlFor="eatzy-attr-start">Start date</Label>
            <Input
              id="eatzy-attr-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-40"
              data-testid="input-eatzy-attr-start"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eatzy-attr-end">End date</Label>
            <Input
              id="eatzy-attr-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-40"
              data-testid="input-eatzy-attr-end"
            />
          </div>
          <Button
            onClick={handleFetch}
            disabled={isFetching || !start || !end}
            data-testid="button-eatzy-attr-fetch"
          >
            {isFetching ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {hasFetched ? 'Refresh' : 'Fetch Data'}
          </Button>
        </div>

        {/* Error state */}
        {hasError && !isFetching && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            Failed to load data from Eatzy. The analytics endpoint may not be available, or the
            date range returned no results.
          </div>
        )}

        {/* Orders by source */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <PackageCheck className="w-4 h-4 text-muted-foreground" />
            Orders by Source
          </h3>
          {!hasFetched && (
            <p className="text-sm text-muted-foreground">Select a date range and fetch data to view results.</p>
          )}
          {hasFetched && ordersQuery.isFetching && <TableSkeleton rows={4} />}
          {hasFetched && !ordersQuery.isFetching && !ordersQuery.isError && orderRows.length === 0 && (
            <EmptyState message="No attributed orders found for this period." />
          )}
          {hasFetched && !ordersQuery.isFetching && !ordersQuery.isError && orderRows.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm" data-testid="table-eatzy-orders-by-source">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Source</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Channel</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Orders</th>
                    {orderRows.some((r) => r.revenue != null) && (
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">Revenue</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orderRows.map((row, i) => (
                    <tr key={i} className="border-b last:border-0 hover-elevate">
                      <td className="px-4 py-2 font-medium">{row.source ?? '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{row.channel ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.count?.toLocaleString() ?? 0}</td>
                      {orderRows.some((r) => r.revenue != null) && (
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                          {row.revenue != null ? `$${Number(row.revenue).toFixed(2)}` : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Signups by source */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Users className="w-4 h-4 text-muted-foreground" />
            Signups by Source
          </h3>
          {!hasFetched && (
            <p className="text-sm text-muted-foreground">Select a date range and fetch data to view results.</p>
          )}
          {hasFetched && signupsQuery.isFetching && <TableSkeleton rows={4} />}
          {hasFetched && !signupsQuery.isFetching && !signupsQuery.isError && signupRows.length === 0 && (
            <EmptyState message="No attributed signups found for this period." />
          )}
          {hasFetched && !signupsQuery.isFetching && !signupsQuery.isError && signupRows.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm" data-testid="table-eatzy-signups-by-source">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Source</th>
                    <th className="px-4 py-2 text-left font-medium text-muted-foreground">Channel</th>
                    <th className="px-4 py-2 text-right font-medium text-muted-foreground">Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {signupRows.map((row, i) => (
                    <tr key={i} className="border-b last:border-0 hover-elevate">
                      <td className="px-4 py-2 font-medium">{row.source ?? '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">{row.channel ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.count?.toLocaleString() ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['/api/admin/stats'],
  });

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers, icon: Users, color: 'text-blue-500' },
    { label: 'Total Reviews', value: stats?.totalReviews, icon: MessageSquare, color: 'text-green-500' },
    { label: 'Restaurants', value: stats?.totalRestaurants, icon: Store, color: 'text-purple-500' },
    { label: 'Pending Reviews', value: stats?.pendingReviews, icon: AlertTriangle, color: 'text-yellow-500' },
    { label: 'Flagged Reviews', value: stats?.flaggedReviews, icon: AlertTriangle, color: 'text-red-500' },
    { label: 'Kudoz Awarded', value: stats?.totalKudozAwarded?.toLocaleString(), icon: Award, color: 'text-orange-500' },
    { label: 'Active Users (7d)', value: stats?.activeUsers7d, icon: TrendingUp, color: 'text-cyan-500' },
    { label: 'New Users Today', value: stats?.newUsersToday, icon: Users, color: 'text-emerald-500' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Admin Dashboard</h1>
          <p className="text-muted-foreground">Overview of platform metrics and activity</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} data-testid={`card-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-20" />
                ) : (
                  <p className="text-2xl font-bold">{stat.value ?? 0}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">Activity feed coming soon...</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-muted-foreground text-sm">Quick actions coming soon...</p>
            </CardContent>
          </Card>
        </div>

        <EatzyAttributionPanel />
      </div>
    </AdminLayout>
  );
}
